import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CommunicationService } from '../http/communication.service';
import {
  BrandSearchResponse,
  SignupRequest,
  SignupResponse,
  UpdateBrandsRequest,
  UpdateBrandsResponse,
  SignupOptionsResponse,
  UpdateProfessionalDetailsRequest,
  UpdateProfessionalDetailsResponse,
  CompleteSignupResponse,
  GetSignupRequest,
  GetSignupResponse
} from '../shared/types/freemium.types';
import {
  FreemiumFields,
  HubspotFields,
  HubspotFormPhase,
} from './hubspot/hubspot.types';
import {HubspotService} from './hubspot/hubspot.service';
import { FreemiumApiPaths } from './types';

@Injectable({
  providedIn: 'root'
})
export class FreemiumService {
  private communicationService = inject(CommunicationService);
  private hubspotService = inject(HubspotService);

  updateHubspotFields(fields: Partial<FreemiumFields>): void {
    this.hubspotService.updateFields(fields);
  }

  getHubspotFields(): FreemiumFields {
    return this.hubspotService.getFields();
  }

  searchBrands(term: string): Observable<BrandSearchResponse> {
    return this.communicationService.httpGet<BrandSearchResponse>(FreemiumApiPaths.BRANDS_SEARCH, {
      params: { term }
    });
  }

  getTrendingBrands(limit: number = 5): Observable<BrandSearchResponse> {
    return this.communicationService.httpGet<BrandSearchResponse>(FreemiumApiPaths.TRENDING_BRANDS, {
      params: { limit }
    });
  }

  signup(payload: SignupRequest): Observable<SignupResponse> {
    return this.communicationService.httpPost<SignupResponse>(FreemiumApiPaths.SIGNUP, payload, { skipAuth: true });
  }

  getTopCompetitors(brandKey: string): Observable<BrandSearchResponse> {
    return this.communicationService.httpGet<BrandSearchResponse>(FreemiumApiPaths.TOP_COMPETITORS, {
      params: { brandKey }
    });
  }

  getSignupOptions(): Observable<SignupOptionsResponse> {
    return this.communicationService.httpGet<SignupOptionsResponse>(FreemiumApiPaths.OPTIONS, {
      skipAuth: true
    });
  }

  getTrialUser(email: string, signupToken: string, id: string): Observable<GetSignupResponse> {
    const params: GetSignupRequest = {
      email,
      signupToken
    };
    return this.communicationService.httpGet<GetSignupResponse>(`${FreemiumApiPaths.SIGNUP}/${id}`, {
      params,
      skipAuth: true
    });
  }

  updateProfessionalDetails(companyName: string, jobTitle: string): Observable<UpdateProfessionalDetailsResponse> {
    const payload: UpdateProfessionalDetailsRequest = {
      companyName,
      jobTitle
    };
    return this.communicationService.httpPut<UpdateProfessionalDetailsResponse>(FreemiumApiPaths.UPDATE_PROFESSIONAL_DETAILS, payload);
  }

  completeSignup(): Observable<CompleteSignupResponse> {
    return this.communicationService.httpPost<CompleteSignupResponse>(FreemiumApiPaths.COMPLETE, {});
  }

  updateBrands(payload: UpdateBrandsRequest): Observable<UpdateBrandsResponse> {
    return this.communicationService.httpPost<UpdateBrandsResponse>(FreemiumApiPaths.UPDATE_BRANDS, payload);
  }

  sendPasswordResetEmail(email: string): Observable<{ message: string }> {
    return this.communicationService.httpPost<{ message: string }>(FreemiumApiPaths.RESET_EMAIL, {email});
  }

  async sendToHubSpot(phase: HubspotFormPhase, form: HubspotFields, url: string): Promise<void> {
    return this.hubspotService.submitToHubspot(phase, form, url);
  }
}
